import React from 'react';
import Joi from 'joi-browser';
import * as _ from 'lodash';
import { validationByJoi } from '../utilities/commonValidation';
import regex from '../utilities/validateRegex';
import { GroupSettingService } from '../services/GroupSetting';
import SessionStorageService from '../services/utilities/SessionStorageService.js';
import { Delete, Edited } from './modals/Commons';
import slideLoader from '../utilities/slideLoader';
import dialogLoader from '../utilities/dialogLoader';
import { NeverDeletePartnerCompany } from '../components/FieldTreeDialog';
import { CloseEditView } from '../components/CloseEditView';
import EventMg from '../utilities/EventManager';
import Helmet from 'react-helmet';
import { HEADER_CONSTANTS } from '../utilities/constants';
import { messageUtility } from '../utilities/messageUtility';
import {
  getErrorMessage,
  DropdownSelect,
  ApiCache,
  formatForemanName,
} from './GroupSettingCommon';
import { convertDate } from './common/MasterParts.jsx';
import fetchMg from '../utilities/fetchManager';
import { deleteControlCode } from '../utilities/stringUtil';
import { ConferencePageHeader } from '../components/PageHeader';
import {
  FormElement,
  FormList,
  FormListItem,
} from '../components/CommonFormComponents';
import { RadioButtonIcon } from '../components/icons/RadioButtonIcon';
import { TrashIcon } from '../components/icons/TrashIcon';

const MY_EVENT = {
  LOAD_SETTING_GROUP: 'load_setting_group', // 一覧取得
};

/**
 * グループ設定編集
 *
 * @export
 * @class GroupSettingEdit
 * @extends {React.Component}
 */
export class GroupSettingEdit extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      // 元請担当者(ドロップダウン)
      managers: [],
      // 所属協力会社(ドロップダウン)
      partnerCompanies: [],
      // 所属職長(ドロップダウン)
      foremen: [],
      fieldId: SessionStorageService.getItem('field_id'),
      // グループ設定ID
      groupSettingId: props.view.state.id,
      formValues: {
        display_flag: 1,
        name: '',
        kana: '',
      },
      errors: {},
      requiredInput: {
        columns: ['display_flag', 'name', 'kana'],
        count: 3,
        total: 3,
      },
      created_at: '',
      updated_at: '',
      isLoading: false, //「削除」ボタンの連打防止Flag
    };

    this.updatingFlag = false; // 多重更新防止Flag

    this.events = {
      modal: {
        deleteModal: this.deleteModal.bind(this),
        delete: this.deleteGroupSetting.bind(this),
      },
      form: {
        // 元請担当者
        addManagerForm: this.addManagerForm.bind(this),
        removeManagerForm: this.removeManagerForm.bind(this),
        setManagerForm: this.setManagerForm.bind(this),
        // 協力会社
        addPartnerCompanyForm: this.addPartnerCompanyForm.bind(this),
        removePartnerCompanyForm: this.removePartnerCompanyForm.bind(this),
        setPartnerCompanyForm: this.setPartnerCompanyForm.bind(this),
        // 所属職長
        addForemanForm: this.addForemanForm.bind(this),
        removeForemanForm: this.removeForemanForm.bind(this),
        setForemanForm: this.setForemanForm.bind(this),
      },
    };

    // 元請担当者
    this.managerDefaultValues = {
      id: null,
      fullname: null,
      managers: [],
    };

    // 協力会社
    this.partnerCompanyDefaultValues = {
      id: null,
      name: null,
      partnerCompanies: [],
    };

    // 所属職長
    this.foremanDefaultValues = {
      id: null,
      fullname: null,
    };

    this.updateGroupSetting = this.updateGroupSetting.bind(this);
    this.groupSettingList = this.groupSettingList.bind(this);

    this.schema = Joi.object().keys({
      display_flag: Joi.number()
        .min(0)
        .max(1)
        .required(),
      name: Joi.string()
        .max(255)
        .required(),
      kana: Joi.string()
        .max(255)
        .regex(regex.pattern.hiragana)
        .required(),
    });

    this.partnerCompanyCache = new ApiCache();
    this.foremanCache = new ApiCache();
  }

  /**
   * 元請担当者の追加
   *
   * @memberof GroupSettingEdit
   */
  addManagerForm = () => {
    const managers = this.state.managers;
    managers.push(this.managerDefaultValues);
    this.setState({ managers });
  };

  /**
   * 元請担当者の削除
   *
   * @memberof GroupSettingEdit
   */
  removeManagerForm = index => {
    const managers = this.state.managers;
    managers.splice(index, 1);
    this.setState({ managers });
  };

  /**
   * 元請担当者の設定
   *
   * @memberof GroupSettingEdit
   */
  setManagerForm = (name, item, index) => {
    const managers = this.state.managers;
    managers[index] = {
      id: item.id,
      fullname: item.fullname,
    };

    this.setState({ managers });
  };

  /**
   * 所属協力会社の追加
   *
   * @memberof GroupSettingEdit
   */
  addPartnerCompanyForm = () => {
    const partnerCompanies = this.state.partnerCompanies;
    partnerCompanies.push(this.partnerCompanyDefaultValues);
    this.setState({ partnerCompanies });
  };

  /**
   * 所属協力会社の削除
   *
   * @memberof GroupSettingEdit
   */
  removePartnerCompanyForm = index => {
    const partnerCompanies = this.state.partnerCompanies;
    partnerCompanies.splice(index, 1);
    this.setState({ partnerCompanies });
  };

  /**
   * 所属協力会社の設定
   *
   * @memberof GroupSettingEdit
   */
  setPartnerCompanyForm = (name, item, index) => {
    const partnerCompanies = this.state.partnerCompanies;
    partnerCompanies[index] = {
      // item.idではないことに注意
      id: item.field_tree_id,
      // item.nameではないことに注意
      name: item.company_name,
    };

    this.setState({ partnerCompanies });
  };

  /**
   * 所属職長の追加
   *
   * @memberof GroupSettingEdit
   */
  addForemanForm = () => {
    this.setState({
      foremen: this.state.foremen.concat(this.foremanDefaultValues),
    });
  };

  /**
   * 所属職長の削除
   *
   * @memberof GroupSettingEdit
   */
  removeForemanForm = targetIndex => {
    this.setState({
      foremen:
        _.filter(this.state.foremen, (foreman, i) => targetIndex !== i) || [],
    });
  };

  /**
   * 所属職長の設定
   *
   * @memberof GroupSettingEdit
   */
  setForemanForm = (name, item, index) => {
    const foremen = [...this.state.foremen];
    foremen[index] = item;

    this.setState({ foremen });
  };

  componentDidMount() {
    this.getEditingGroupSetting(() => {});
  }

  /**
   * フォームの内容の変化
   *
   * @param {*} event
   * @memberof GroupSettingEdit
   */
  onChangeFormValues(event) {
    const formValues = { ...this.state.formValues };
    formValues[event.target.name] = deleteControlCode(event.target.value);
    const result = validationByJoi(this.schema, formValues);
    this.setRequiredCount(result);
    this.setState({
      formValues,
    });
  }

  /**
   * 重複チェック
   *
   * @param {*} name
   * @param {*} errors
   * @returns
   * @memberof GroupSettingEdit
   */
  duplicateCheck(name, errors) {
    return GroupSettingService.list(this.state.fieldId, { is_limit: 0 }).then(
      resp => {
        if (resp.result) {
          const listData = resp.data.entities.field_groups;
          // 自分以外の名称で重複があるかチェック
          if (
            _.some(
              listData,
              groupSetting =>
                groupSetting.id !== this.state.groupSettingId &&
                groupSetting.name === name
            )
          ) {
            errors.name = messageUtility.getMessage('E-001-005');
          }

          this.setState({
            errors,
          });
        }
      }
    );
  }

  /**
   * 必須項目の入力状況監視
   *
   * @param validateResult {object}
   * @param errors {object}
   * @memberof GroupSettingEdit
   */
  setRequiredCount(validateResult, errors) {
    const requiredInput = { ...this.state.requiredInput };
    if (!errors) {
      errors = { ...this.state.errors };
    }

    // 合計 - 必須エラーの数 = 実際に入力された値
    requiredInput.count =
      requiredInput.total - _.size({ ...errors, ...validateResult.messages });
    this.setState({ requiredInput });
  }

  /**
   * バリデーション
   *
   * @param {*} event
   * @returns
   * @memberof GroupSettingEdit
   */
  validate(event) {
    const params = this.state.formValues;
    const result = validationByJoi(this.schema, params);
    const errors = this.state.errors;

    // 単数or複数判定
    if (event) {
      if (result.messages[event.target.name]) {
        errors[event.target.name] = result.messages[event.target.name];
      } else if (errors[event.target.name]) {
        delete errors[event.target.name];
      }

      this.setState({ errors });

      if (event.target.name === 'name') {
        this.duplicateCheck(event.target.value, errors).then(() => {
          this.setRequiredCount(result, errors);
        });
      } else {
        this.setRequiredCount(result, errors);
      }

      return Object.keys(this.state.errors).length;
    } else {
      // 非同期処理が終わってから返す
      return this.duplicateCheck(params.name, result.messages).then(() => {
        return Object.keys(this.state.errors).length;
      });
    }
  }

  /**
   * 更新
   *
   * @memberof GroupSettingEdit
   */
  updateGroupSetting() {
    // 多重更新防止
    if (this.updatingFlag === true) {
      return;
    }
    this.updatingFlag = true;

    this.validate().then(validate => {
      if (validate !== 0) {
        this.updatingFlag = false;
        return;
      }
      const fieldId = this.state.fieldId;
      const id = this.state.groupSettingId;
      let params = this.state.formValues;

      // idがnullではないものを抽出しidの配列を作成
      const managerIds = _.filter(this.state.managers, item => {
        return item['id'] !== null;
      }).map(item => {
        return item.id;
      });

      // idがnullではないものを抽出しidの配列を作成
      const partnerCompanyIds = _.filter(this.state.partnerCompanies, item => {
        return item['id'] !== null;
      }).map(item => {
        return item.id;
      });

      // idがnullではないものを抽出しidの配列を作成
      const foremanIds = _.filter(this.state.foremen, item => {
        return item['id'] !== null;
      }).map(item => {
        return item.id;
      });

      params = _.assign(params, { manager_ids: managerIds });
      params = _.assign(params, {
        field_tree_ids: partnerCompanyIds,
      });
      params = _.assign(params, { foreman_ids: foremanIds });
      GroupSettingService.update(fieldId, id, params)
        .then(resp => {
          if (resp.result) {
            this.groupSettingList();
            // 更新完了ダイアログ
            dialogLoader.show(Edited);
          }
        })
        .finally(() => {
          this.updatingFlag = false;
        });
    });
  }

  /**
   * 編集
   *
   * @memberof GroupSettingEdit
   */
  getEditingGroupSetting() {
    const fieldId = this.state.fieldId;
    const id = this.state.groupSettingId;
    GroupSettingService.show(fieldId, id).then(async resp => {
      if (resp.result) {
        let groupSetting = resp.data.entities.field_group;
        let created_at = resp.data.entities.field_group.created_at;
        let updated_at = resp.data.entities.field_group.updated_at;
        const formValues = {
          // 0:未使用, 1:使用中
          display_flag: groupSetting.display_flag,
          name: groupSetting.name,
          kana: groupSetting.kana,
        };

        // 元請担当者(ドロップダウン)
        const managers = _.map(resp.data.entities.managers, manager => {
          // idとfullnameのみ使う
          return { id: manager.id, fullname: manager.fullname };
        });

        // 所属協力会社(ドロップダウン)
        const partnerCompanies = _.map(
          resp.data.entities.partner_companies,
          partnerCompany => {
            // idとnameのみ使う
            return {
              id: partnerCompany.field_tree_id,
              name: partnerCompany.name,
            };
          }
        );

        // 所属職長(ドロップダウン)
        const foremenTemp = _.map(resp.data.entities.foremen, foreman => {
          // idとfullnameのみ使う
          return { id: foreman.id, fullname: foreman.fullname };
        });
        const foremen = await this.getForemenCompnay(foremenTemp);

        // ドロップダウンはデフォルトで1つ入るため編集対象が空ならデフォルトを入れておく
        if (managers.length === 0) {
          managers.push(this.managerDefaultValues);
        }

        if (partnerCompanies.length === 0) {
          partnerCompanies.push(this.partnerCompanyDefaultValues);
        }

        if (foremen.length === 0) {
          foremen.push(this.foremanDefaultValues);
        }

        this.setState({
          formValues,
          // 3つのドロップダウン
          managers,
          partnerCompanies,
          foremen,
          created_at,
          updated_at,
        });
      }
    });
  }

  // 職長の会社名取得
  getForemenCompnay = async foremen => {
    return await Promise.all(
      _.map(foremen, async foreman => {
        const data = await fetchMg
          .GetAjax(`/api/private/users/company_partner_user_list`, {
            field_id: this.state.fieldId,
            id: foreman.id,
          })
          .catch(() => ({ result: false }));
        if (data.result) {
          return data.data.entities.user_lists[0];
        } else {
          return foreman;
        }
      })
    );
  };

  /**
   * 削除確認モーダル
   *
   * @memberof GroupSettingEdit
   */
  deleteModal() {
    dialogLoader.show(Delete, {
      state: {
        deleteId: this.state.groupSettingId,
        callbacks: this.events,
      },
    });
  }

  /**
   * 実際の削除
   *
   * @memberof GroupSettingEdit
   */
  deleteGroupSetting() {
    // BLDE_V2-50769: isLoadingがTrueならdeleteGroupSettingが発生しないため、連打防止
    if (!this.state.isLoading) {
      this.setState({ isLoading: true }, () => {
        const fieldId = this.state.fieldId;
        const id = this.state.groupSettingId;
        GroupSettingService.del(fieldId, id)
          .then(resp => {
            if (resp.result) {
              this.groupSettingList();
              dialogLoader.close();

              if (resp.data.entities.error_type === 1) {
                // データの削除不可ダイアログ
                dialogLoader.show(NeverDeletePartnerCompany, {
                  callback: {
                    done: () => {
                      dialogLoader.close();
                    },
                  },
                });
              }
            }
          })
          .finally(() => {
            // 削除が終わったら連打禁止を解除
            this.setState({ isLoading: false });
          });
      });
    }
  }

  /**
   * グループ設定リスト
   *
   * @memberof GroupSettingEdit
   */
  groupSettingList() {
    EventMg.trigger(MY_EVENT.LOAD_SETTING_GROUP);
    slideLoader.close(this.props.vw);
  }

  /**
   * 右上のXの閉じるボタン
   *
   * @memberof GroupSettingEdit
   */
  close() {
    dialogLoader.show(CloseEditView, {
      callback: {
        close: () => {
          dialogLoader.close();
        },
        clear: () => {
          dialogLoader.close();
          EventMg.trigger(MY_EVENT.LOAD_SETTING_GROUP);
          slideLoader.close(this.props.vw);
        },
      },
    });
  }

  render() {
    return (
      <div className='slide-view show'>
        <div className='slide-view__view'>
          <Helmet>
            <title>{HEADER_CONSTANTS.conference} - グループ編集</title>
          </Helmet>
          <ConferencePageHeader
            title={'グループ編集'}
            onClose={() => this.close()}
          />
          <div className='main__body main__body__new-slide--fixed'>
            <div id='setting-header' className='setting-header'>
              <div className='setting-header__item'>
                <button
                  className='btn--effective'
                  onClick={this.updateGroupSetting}
                >
                  更新
                </button>
                <button
                  className='btn--negative'
                  onClick={() => this.deleteModal()}
                >
                  削除
                </button>
                <div className='--fx-auto' />
                <div className='setting-header__end-item'>
                  {`必須項目の入力数 ${this.state.requiredInput.count}/${this.state.requiredInput.total}`}
                </div>
              </div>
            </div>
            <div className='setting-main__table'>
              <FormList isSpace={true}>
                <FormListItem label='ステータス' required={true}>
                  <FormElement>
                    <div className='form-list-radio'>
                      <div className='form-list-radio__item'>
                        <label className='radio'>
                          <input
                            name='display_flag'
                            type='radio'
                            checked={
                              parseInt(this.state.formValues.display_flag) === 1
                            }
                            value='1'
                            onChange={e => {
                              this.onChangeFormValues(e);
                            }}
                          />
                          <RadioButtonIcon className='icon--2x' />
                          <span>使用中</span>
                        </label>
                      </div>
                      <div className='form-list-radio__item'>
                        <label className='radio'>
                          <input
                            name='display_flag'
                            type='radio'
                            checked={
                              parseInt(this.state.formValues.display_flag) === 0
                            }
                            value='0'
                            onChange={e => {
                              this.onChangeFormValues(e);
                            }}
                          />
                          <RadioButtonIcon className='icon--2x' />
                          <span>未使用</span>
                        </label>
                      </div>
                    </div>
                  </FormElement>
                </FormListItem>
                <FormListItem label='グループ名' required={true}>
                  <FormElement
                    validate={getErrorMessage(this.state.errors, 'name')}
                  >
                    <input
                      type='text'
                      name='name'
                      value={this.state.formValues.name}
                      maxLength={255}
                      onChange={event => this.onChangeFormValues(event)}
                      onBlur={event => this.validate(event)} // バリデーション
                      placeholder='グループ名を入力'
                    />
                  </FormElement>
                </FormListItem>
                <FormListItem label='ふりがな' required={true}>
                  <FormElement
                    validate={getErrorMessage(this.state.errors, 'kana')}
                  >
                    <input
                      type='text'
                      name='kana'
                      value={this.state.formValues.kana}
                      maxLength={255}
                      onChange={event => this.onChangeFormValues(event)}
                      onBlur={event => this.validate(event)} // バリデーション
                      placeholder='ふりがなを入力'
                    />
                  </FormElement>
                </FormListItem>
                <FormListItem label='所属協力会社'>
                  <FormElement>
                    <div className='form-container'>
                      {this.state.partnerCompanies.map(
                        (partnerCompany, index) => {
                          return (
                            <div
                              key={index}
                              className='form-list__dropdown-item'
                            >
                              {/* 施工体系図の協力会社のプルダウンと同じ */}
                              <DropdownSelect
                                label='未選択'
                                name='partner_companies'
                                placeholder='所属協力会社を選択'
                                apiPath={`/api/private/fields/${this.state.fieldId}/field_trees/tree_company_branches`}
                                entitiesKey='field_trees'
                                itemKey='company_name'
                                searchKey='name'
                                setKey='field_tree_id'
                                value={partnerCompany.id}
                                valueName={partnerCompany.name}
                                widthClass='dropdown__item--wide'
                                requiredParams={{
                                  primary_display_flag: 1,
                                }}
                                list={partnerCompany.partnerCompanies}
                                changeEvent={(name, item) => {
                                  this.events.form.setPartnerCompanyForm(
                                    name,
                                    item,
                                    index
                                  );
                                }}
                                apiCache={this.partnerCompanyCache}
                              />
                              <button
                                className='form-list__element--btn_delete btn-icon--xs'
                                onClick={() =>
                                  this.events.form.removePartnerCompanyForm(
                                    index
                                  )
                                }
                              >
                                <TrashIcon className='icon--2_4x form-list__trash-icon' />
                              </button>
                            </div>
                          );
                        }
                      )}
                      <div
                        className={`${!_.isEmpty(this.state.partnerCompanies) &&
                          'form-container__item'}`}
                      >
                        <button
                          className='btn--sub'
                          onClick={() =>
                            this.events.form.addPartnerCompanyForm()
                          }
                        >
                          追加する
                        </button>
                      </div>
                    </div>
                  </FormElement>
                </FormListItem>
                <FormListItem label='所属職長'>
                  <FormElement>
                    <div className='form-container'>
                      {this.state.foremen.map((foreman, index) => {
                        return (
                          <div key={index} className='form-list__dropdown-item'>
                            <DropdownSelect
                              label='未選択'
                              name='foremen'
                              placeholder='所属職長を選択'
                              // 施工体系図の対応現場の協力会社の全ユーザ
                              apiPath={`/api/private/users/company_partner_user_list`}
                              entitiesKey='user_lists'
                              itemKey='fullname'
                              searchKey='fullname'
                              setKey='id'
                              value={foreman.id}
                              valueName={formatForemanName(foreman)}
                              viewFlg={true}
                              editFlag={true}
                              widthClass='dropdown__item--wide'
                              requiredParams={{
                                field_id: this.state.fieldId,
                              }}
                              list={foreman.foremen}
                              changeEvent={(name, item) => {
                                this.events.form.setForemanForm(
                                  name,
                                  item,
                                  index
                                );
                              }}
                              itemNameFormatter={formatForemanName}
                              apiCache={this.foremanCache}
                            />
                            <button
                              className='form-list__element--btn_delete btn-icon--xs'
                              onClick={() =>
                                this.events.form.removeForemanForm(index)
                              }
                            >
                              <TrashIcon className='icon--2_4x form-list__trash-icon' />
                            </button>
                          </div>
                        );
                      })}
                      <div
                        className={`${!_.isEmpty(this.state.foremen) &&
                          'form-container__item'}`}
                      >
                        <button
                          className='btn--sub'
                          onClick={() => this.events.form.addForemanForm()}
                        >
                          追加する
                        </button>
                      </div>
                    </div>
                  </FormElement>
                </FormListItem>
                <FormListItem label='登録日時'>
                  <FormElement>
                    {convertDate(this.state.created_at)}
                  </FormElement>
                </FormListItem>
                <FormListItem label='更新日時'>
                  <FormElement>
                    {convertDate(this.state.updated_at)}
                  </FormElement>
                </FormListItem>
              </FormList>
            </div>
          </div>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/pages/GroupSettingEdit.jsx